<template>
  <main class="listing">
    <!-- Nothing at all until the record arrives. Flashing "not found" at somebody whose link
         is perfectly good, for as long as a round trip takes, is worse than a blank moment. -->
    <section v-if="loading" class="section missing-section" aria-busy="true">
      <div class="shell">
        <p class="loading-note">Loading this machine…</p>
      </div>
    </section>

    <section v-else-if="!unit" class="section missing-section">
      <div class="shell">
        <!-- Three different things went wrong, so three different answers. A machine that sold
             and was taken down is the common one and deserves a straight reply: the link is
             out in the world for good, and a dead page reads as a broken company where "this
             one sold" reads as a machine that sold. -->
        <template v-if="gone">
          <h1>This listing is no longer available</h1>
          <p>
            It has been taken down, so there is nothing more to show here. There is usually
            something else moving — tell us what you are looking for.
          </p>
        </template>
        <template v-else-if="loadError">
          <h1>We could not load this machine</h1>
          <p>{{ loadError }} Call {{ brand.phone }} and we will tell you about it directly.</p>
        </template>
        <template v-else>
          <h1>Listing not found</h1>
          <p>
            That machine is not listed. It may have sold, or the link may have been mistyped.
          </p>
        </template>
        <router-link class="btn btn-primary" to="/forsale">Back to sale units</router-link>
      </div>
    </section>

    <section v-else class="section listing-section">
      <article class="shell listing-body">
        <router-link class="back-link" to="/forsale">← All sale units</router-link>

        <h1 class="listing-hero">{{ unit.title }}</h1>

        <!-- No listing number anywhere on this page. The machine is the identity; a second
             four-digit series alongside CFM would only make a customer guess which one they
             are holding. -->
        <header class="listing-header">
          <div class="machine-identity">
            <p class="eyebrow-mono">{{ unit.status }}</p>
            <p>{{ unit.kind }}</p>
          </div>
        </header>

        <section class="price-card" aria-labelledby="price-title">
          <p>Asking price</p>
          <h2 id="price-title">{{ unit.price || 'Contact for price' }}</h2>
          <span>{{ contactNote }}</span>
        </section>

        <!-- Photographs are the listing. Until we have real ones, the frame says so rather
             than quietly collapsing, so a half-finished listing is obvious to us. -->
        <section class="photo-strip" aria-label="Machine photographs">
          <template v-if="unit.photos.length">
            <figure v-for="photo in unit.photos" :key="photo.src">
              <img :src="photo.src" :alt="photo.alt" loading="lazy" />
            </figure>
          </template>
          <p v-else class="photo-empty">Photographs are being taken — call and we will send them straight over.</p>
        </section>

        <section class="listing-stats" aria-label="Machine at a glance">
          <div v-if="unit.reading">
            <p>{{ unit.reading.unit === 'miles' ? 'Odometer' : 'Hour meter' }}</p>
            <strong>{{ unit.reading.value }}</strong>
            <span>{{ unit.reading.unit }} at last reading</span>
          </div>
          <div v-if="unit.hours != null && unit.mileage != null"><p>Odometer</p><strong>{{ unit.mileage }}</strong><span>miles at last reading</span></div>
          <div v-if="unit.year || unit.make || unit.model">
            <p>Year / make / model</p>
            <strong>{{ unit.year }}</strong>
            <span>{{ unit.make }} {{ unit.model }}</span>
          </div>
          <div>
            <p>Located</p>
            <strong>{{ unit.region }}</strong>
            <span>Viewings arranged through Centerfire</span>
          </div>
        </section>

        <section class="listing-grid">
          <article class="listing-panel">
            <div class="panel-heading">
              <p class="eyebrow-mono">The machine</p>
              <h2>What you are looking at</h2>
            </div>

            <p v-if="unit.blurb" class="description">{{ unit.blurb }}</p>
            <ul class="highlights">
              <li v-for="point in unit.highlights" :key="point">
                <span class="bullet-dot" aria-hidden="true"></span>
                <span>{{ point }}</span>
              </li>
            </ul>

            <p class="condition">{{ unit.condition }}</p>
          </article>

          <article class="listing-panel contact-panel">
            <div class="panel-heading">
              <p class="eyebrow-mono">Interested?</p>
              <h2>Contact Centerfire</h2>
            </div>

            <!-- Anonymity is the seller's choice, so this promises the process, not
                 secrecy: "we will not tell you whose it is" read as extreme and was untrue
                 for a seller who chooses to be named (Daniel 2026-08-30). -->
            <p class="contact-copy">
              Every question, offer, and viewing goes through us. We will tell you everything
              we know about the machine; the seller stays unnamed unless they have chosen
              otherwise.
            </p>

            <router-link class="btn btn-primary contact-button" :to="contactRoute">
              Ask about this machine
            </router-link>

            <a class="call-link" :href="phoneHref">Or call {{ brand.phone }}</a>
          </article>
        </section>

        <!-- Carried over from the machine's CFM record, already stripped of everything that
             identifies the owner: what was done, when, and at what reading. The CFM record
             itself stays behind its PIN - this page never asks for it. -->
        <section class="listing-panel maintenance-panel">
          <div class="panel-heading">
            <p class="eyebrow-mono">Maintenance record</p>
            <h2>{{ unit.maintenance ? 'Maintained by Centerfire' : 'Not on our program' }}</h2>
          </div>

          <!-- The inline table became a PDF (Daniel 2026-09-09). A buyer gets a document they
               can keep and hand to their insurer, and the page stops carrying a hand-copied
               duplicate of a record that lives in the ops system. What is left here is the
               one-line provenance sentence and the link.

               The PDF is GENERATED from the machine's customer-published events - the same
               boundary the owner's own printed sheet uses - so a line reaches a buyer only if
               it was already published to the owner. One visibility flag, deliberately. It is
               attached only when the seller asked for it, which is recorded. -->
          <template v-if="unit.maintenance">
            <p v-if="unit.maintenance.summary" class="maintenance-summary">{{ unit.maintenance.summary }}</p>

            <!-- No "written by the technician who did the work" claim: seats get subbed to
                 an upholstery shop, tires get mounted at a bike shop - the tech does not
                 perform every inch of the work, so that was a bad promise to make (Daniel
                 2026-08-30). The record's value is WHAT was done and when, not who held the
                 wrench. -->
            <p class="maintenance-note">
              Under our program this record follows the machine to its next owner.
            </p>

            <a
              v-if="unit.maintenance.logUrl"
              class="btn btn-primary record-download"
              :href="unit.maintenance.logUrl"
              target="_blank"
              rel="noopener"
            >Download the maintenance record (PDF)</a>
          </template>

          <p v-else class="maintenance-note">
            This machine has not been on a Centerfire maintenance program, so we have no
            service record for it. We can inspect it before you buy — ask us.
          </p>
        </section>
      </article>
    </section>
  </main>
</template>

<script>
import { brand } from '@/data/content';
import { findListing } from '@/data/machinesForSale';

export default {
  name: 'MachineForSaleView',
  data() {
    return {
      brand,
      unit: null,
      loading: true,
      // 410 from the endpoint: the machine sold and the listing was archived. Distinct from a
      // 404, and the page says so rather than pretending the link was always wrong.
      gone: false,
      loadError: '',
      // The slug whose fetch is allowed to write to this component. Not rendered.
      pending: '',
    };
  },
  computed: {
    phoneHref() {
      return `tel:${this.brand.phone.replace(/\D/g, '')}`;
    },
    // Carries the listing's slug into the request form so we know which machine an enquiry
    // is about without the buyer having to describe it. The slug SELECTS the listing there;
    // the wording is read from our own record, never from the URL.
    contactRoute() {
      return { path: '/services-request', query: { about: this.unit.slug } };
    },
    contactNote() {
      if (this.unit.status === 'Sold') return 'This machine has sold';
      if (this.unit.status === 'Sale pending') return 'Sale pending — ask to be second in line';
      return 'Inquiries handled by Centerfire';
    },
  },
  created() {
    this.load();
  },
  watch: {
    '$route.params.slug': 'load',
  },
  methods: {
    async load() {
      this.loading = true;
      this.gone = false;
      this.loadError = '';
      this.unit = null;
      // Fetching is async and the slug is watched, so two quick navigations can resolve out
      // of order and leave the previous machine on screen under the new URL. Only the newest
      // request is allowed to write.
      const slug = this.$route.params.slug;
      this.pending = slug;
      // The slug is read from the URL and used only to SELECT a record. Nothing a visitor
      // types is echoed back into the page - every word rendered below comes from our own
      // record, which is why this is a lookup and not a template fill.
      const { listing, gone, error } = await findListing(slug);
      if (this.pending !== slug) return;
      this.unit = listing;
      this.gone = Boolean(gone);
      this.loadError = error || '';
      this.loading = false;
      // Listings exist to be FOUND, and the title is the line a search result, bookmark, or
      // link preview shows - so it names the machine, not the template. Runs after the
      // router's afterEach set the generic meta title, which remains the fallback.
      if (this.unit) {
        document.title = `Centerfire Services | ${this.unit.title} — For Sale`;
      }
    },
  },
};
</script>

<style scoped>
.listing {
  min-height: 78vh;
  background:
    radial-gradient(circle at 50% 12%, rgba(255, 90, 2, 0.14), transparent 28%),
    var(--black);
}

.listing-section,
.missing-section {
  padding-top: clamp(9rem, 13vw, 11rem);
}

.missing-section h1 {
  font-size: clamp(2.4rem, 5vw, 4rem);
  text-transform: uppercase;
}

.missing-section p {
  max-width: 52ch;
  margin: 1rem 0 1.8rem;
  color: rgba(255, 255, 255, 0.78);
}

.listing-body {
  display: grid;
  gap: 1rem;
}

.back-link {
  justify-self: start;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.back-link:hover,
.back-link:focus-visible {
  color: var(--orange);
}

.listing-hero {
  margin: 0.4rem 0 1.4rem;
  font-size: clamp(2.6rem, 6vw, 5.4rem);
  line-height: 0.95;
  text-align: center;
  text-transform: uppercase;
}

.listing-header {
  display: flex;
  gap: 2rem;
  align-items: end;
  padding-bottom: 1.4rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.machine-identity {
  padding: 0.6rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
}

.machine-identity > p:last-child {
  margin-top: 0.15rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.92rem;
}

.eyebrow-mono {
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.price-card {
  max-width: 820px;
  width: 100%;
  justify-self: center;
  display: grid;
  gap: 0.45rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border: 2px solid var(--orange);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.14), transparent 62%);
  text-align: center;
}

.price-card > p:first-child {
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.price-card h2 {
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 400;
  line-height: 1;
  text-transform: uppercase;
}

.price-card span {
  justify-self: center;
  margin-top: 0.45rem;
  padding: 0.4rem 0.75rem;
  background: var(--orange);
  color: var(--black);
  font-weight: 600;
}

.photo-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.photo-strip figure {
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-sm);
}

.photo-strip img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.photo-empty {
  grid-column: 1 / -1;
  padding: clamp(2rem, 6vw, 4rem) 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.28);
  border-radius: var(--radius);
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
}

.listing-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.listing-stats > div {
  min-height: 145px;
  display: grid;
  align-content: start;
  gap: 0.35rem;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.23);
  border-radius: var(--radius-sm);
}

.listing-stats p {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.listing-stats strong {
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.35rem, 2.7vw, 2rem);
  font-weight: 400;
}

.listing-stats span {
  color: rgba(255, 255, 255, 0.67);
  font-size: 0.85rem;
}

.listing-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
  gap: 1rem;
}

.listing-panel {
  padding: clamp(1.3rem, 3vw, 2rem);
  border: 1px solid rgba(255, 255, 255, 0.23);
  border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.74);
}

.panel-heading h2 {
  margin-top: 0.35rem;
  font-size: clamp(1.7rem, 3vw, 2.5rem);
  font-weight: 400;
}

.highlights {
  display: grid;
  gap: 0.6rem;
  margin: 1.4rem 0 0;
  padding: 0;
  list-style: none;
}

.highlights li {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 0.7rem;
}

.bullet-dot {
  width: 0.5rem;
  height: 0.5rem;
  margin-top: 0.45rem;
  border-radius: 50%;
  background: var(--orange);
}

.description { white-space: pre-line; }
.condition {
  margin-top: 1.4rem;
  padding-top: 1.3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.8);
}

.contact-panel {
  display: grid;
  align-content: start;
  gap: 1rem;
  border-color: rgba(255, 90, 2, 0.62);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.1), transparent 56%);
}

.contact-copy {
  color: rgba(255, 255, 255, 0.8);
}

.contact-button {
  width: 100%;
  text-align: center;
}

.call-link {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.92rem;
}

.call-link:hover,
.call-link:focus-visible {
  color: var(--orange);
}

.maintenance-summary {
  margin-top: 1.2rem;
  padding: 0.7rem 0.9rem;
  border-left: 3px solid var(--orange);
  background: rgba(255, 255, 255, 0.04);
}

.maintenance-note {
  margin-top: 1.2rem;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.86rem;
}

.record-download {
  display: inline-block;
  margin-top: 1.2rem;
}

.loading-note {
  color: rgba(255, 255, 255, 0.62);
}

@media (max-width: 960px) {
  .listing-grid,
  .listing-stats {
    grid-template-columns: 1fr;
  }

  .photo-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

}

@media (max-width: 620px) {
  .listing-header {
    display: grid;
    align-items: start;
  }

  .photo-strip {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
